
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SectionBlockConfiguration } from "@/types/block-configurations";
import {
  BackgroundControls,
  PaddingControls,
  BorderControls,
  ResponsiveControls,
} from "../shared";

interface SectionBlockPropertiesProps {
  config: SectionBlockConfiguration;
  onChange: (config: SectionBlockConfiguration) => void;
}

export function SectionBlockProperties({ config, onChange }: SectionBlockPropertiesProps) {
  const updateField = <K extends keyof SectionBlockConfiguration>(
    field: K,
    value: SectionBlockConfiguration[K]
  ) => {
    onChange({ ...config, [field]: value });
  };

  return (
    <div className="space-y-3">
      {/* Layout Options */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium mb-1 block">Max Width (px)</Label>
          <Input
            type="number"
            value={config.maxWidth ?? 600}
            onChange={(e) => updateField('maxWidth', e.target.value ? parseInt(e.target.value) : undefined)}
            placeholder="600"
            min={200}
            max={1200}
          />
        </div>
        <div className="flex flex-col justify-end gap-1">
          <div className="flex items-center gap-2 h-10">
            <input
              type="checkbox"
              id="full-width"
              checked={config.fullWidth}
              onChange={(e) => updateField('fullWidth', e.target.checked)}
              className="w-4 h-4 rounded border-input"
            />
            <Label htmlFor="full-width" className="text-xs font-medium cursor-pointer">
              Full Width
            </Label>
          </div>
          <p className="text-xs text-muted-foreground leading-tight">
            Background extends edge-to-edge
          </p>
        </div>
      </div>

      {/* Collapsible Sections */}
      <Accordion type="multiple" defaultValue={["background"]} className="w-full">
        
        <AccordionItem value="background">
          <AccordionTrigger>Background</AccordionTrigger>
          <AccordionContent>
            <BackgroundControls
              value={config.background}
              onChange={(background) => updateField('background', background)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="spacing">
          <AccordionTrigger>Spacing</AccordionTrigger>
          <AccordionContent>
            <PaddingControls
              value={config.padding}
              onChange={(padding) => updateField('padding', padding)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="border">
          <AccordionTrigger>Border</AccordionTrigger>
          <AccordionContent>
            <BorderControls
              value={config.border}
              onChange={(border) => updateField('border', border)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="responsive">
          <AccordionTrigger>Responsive</AccordionTrigger>
          <AccordionContent>
            <ResponsiveControls
              value={config.responsive}
              onChange={(responsive) => updateField('responsive', responsive)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="advanced">
          <AccordionTrigger className="text-muted-foreground">Advanced</AccordionTrigger>
          <AccordionContent className="space-y-3 pt-2">
            <div>
              <Label className="text-xs font-medium mb-1 block">Reading direction</Label>
              <Select
                value={config.direction}
                onChange={(e) => updateField('direction', e.target.value as 'ltr' | 'rtl')}
              >
                <option value="ltr">Left to Right (default)</option>
                <option value="rtl">Right to Left (RTL languages)</option>
              </Select>
            </div>
          </AccordionContent>
        </AccordionItem>

      </Accordion>
    </div>
  );
}
